import { Typography } from "@mui/material";
import type { PaletteColor } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { AssistantAvatar } from "@/ui/AssistantAvatar/AssistantAvatar";
import { type AssistantAvatarColor, type AssistantAvatarConfig, AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { resolveBodyPalette } from "@/ui/AssistantAvatar/assistantAvatarPalette";
import type { VisualChoiceOption } from "@/ui/Clickable/VisualChoiceGrid";
import type { AnnieStudioSlot } from "./annieStudioSlots";

/** Values whose tile says "None" in words. */
const NONE_VALUE = "none";

const optionLabel = (t: TFunction, slot: AnnieStudioSlot, value: string): string => t(`annie.avatar.${slot}.${value}`);

export const kebab = (value: string) => value.replaceAll("_", "-");
const optionTestId = (slot: AnnieStudioSlot, value: string) => `annie-studio-${kebab(slot)}-${kebab(value)}`;

/** A still mini avatar: what the design looks like with this one option applied. */
export const miniAvatar = (config: AssistantAvatarConfig, size: number, dataTestId: string) => (
	<AssistantAvatar config={config} mood={AssistantAvatarMood.Idle} size={size} animated={false} dataTestId={dataTestId} />
);

/** Picture tiles for one slot: `preview(value)` is the whole design with that value applied. */
export const pictureOptions = <T extends string>(
	t: TFunction,
	slot: AnnieStudioSlot,
	values: T[],
	preview: (value: T) => AssistantAvatarConfig,
	avatarSize: number,
): VisualChoiceOption<T>[] =>
	values.map((value) => ({
		value,
		label: optionLabel(t, slot, value),
		dataTestId: optionTestId(slot, value),
		content:
			value === NONE_VALUE ? (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 700, color: INK.MUTED }}>
					{t("annie.studio.none")}
				</Typography>
			) : (
				miniAvatar(preview(value), avatarSize, `${optionTestId(slot, value)}-preview`)
			),
	}));

/** Round color swatches for one slot, from a value → CSS background lookup. */
export const swatchOptions = <T extends string>(t: TFunction, slot: AnnieStudioSlot, values: T[], background: (value: T) => string): VisualChoiceOption<T>[] =>
	values.map((value) => ({ value, label: optionLabel(t, slot, value), dataTestId: optionTestId(slot, value), swatch: background(value) }));

/** The body/scrubs swatch: the avatar's own two gradient stops. */
export const bodyColorSwatch = (color: AssistantAvatarColor, primary: PaletteColor): string => {
	const { light, main } = resolveBodyPalette(color, primary);
	return `linear-gradient(${light}, ${main})`;
};
